import {
    FieldTree,
    PathKind,
    SchemaPath,
    SchemaPathRules,
    ValidationError,
    validateTree,
} from '@angular/forms/signals';
import { spanEpoch } from './chronologically-ordered.rule';

/** Which edge of a collection a bound is required to occupy. */
export type CollectionEdge = 'earliest' | 'latest';

/**
 * One instant that fails to respect a bound: either an item that beat it, or — once one has — the
 * bound itself.
 */
export interface BoundConflict {
    /** the offending item's position, or undefined when this reports the bound */
    index?: number;
    /**
     * The instant the offending value may not cross — the bound, for an item that beat it; the
     * worst offending item, for the bound itself.
     *
     * Carried so a message can name it. "Choose a time no later than 11:00" tells the technician
     * what to do; a message with nothing to point at leaves them to search the day for the entry
     * that disagrees.
     */
    limit: string;
}

/** Builds the message for one conflict. */
export type BoundConflictMessage = (conflict: BoundConflict) => string;

/** Options of the {@link boundsCollection} rule. */
export interface BoundsCollectionConfig {
    /** builds the message for each conflict found */
    message?: BoundConflictMessage;
}

/** The bound and the collection it must be the edge of, as {@link boundsCollection} reads them. */
export interface BoundsCollectionFields {
    /** the field required to be the earliest, or the latest, of `items` */
    bound: FieldTree<string>;
    /** the fields the bound must not be beaten by */
    items: ReadonlyArray<FieldTree<string>>;
    /** which edge `bound` is required to occupy */
    edge: CollectionEdge;
}

/**
 * Every item that beats a bound at the edge it is required to occupy: earlier than an "earliest"
 * bound, or later than a "latest" one — plus, once one has, the bound's own conflict against
 * whichever item beats it worst.
 *
 * Every offending item is found rather than only the first, so a collection with more than one out
 * of place is never left with one silently unmarked because another was reported first. The bound
 * is reported once however many items disagree with it, carrying the single worst offender so its
 * message can say by how much it would have to move rather than merely that something disagrees.
 *
 * An unset or unparsable instant is passed over on both sides — whether an empty value is
 * acceptable is a `required` question, and comparing against it would report the same omission a
 * second time under a different name.
 *
 * @param bound the instant required to be the collection's edge
 * @param items the instants it must not be beaten by
 * @param edge which edge `bound` is required to occupy
 * @returns one conflict per offending item, the bound last
 */
export function findBoundConflicts(bound: string, items: readonly string[], edge: CollectionEdge): BoundConflict[] {
    const boundEpoch = spanEpoch(bound);
    if (boundEpoch === undefined) {
        return [];
    }

    const conflicts: BoundConflict[] = [];
    let worst: string | undefined;
    let worstEpoch = edge === 'earliest' ? Infinity : -Infinity;

    items.forEach((item, index) => {
        const itemEpoch = spanEpoch(item);
        if (itemEpoch === undefined) {
            return;
        }

        const beatsBound = edge === 'earliest' ? itemEpoch < boundEpoch : itemEpoch > boundEpoch;
        if (!beatsBound) {
            return;
        }

        conflicts.push({ index, limit: bound });
        const isWorseThanSeen = edge === 'earliest' ? itemEpoch < worstEpoch : itemEpoch > worstEpoch;
        if (isWorseThanSeen) {
            worst = item;
            worstEpoch = itemEpoch;
        }
    });

    if (worst !== undefined) {
        conflicts.push({ limit: worst });
    }

    return conflicts;
}

/**
 * Signal-forms schema rule requiring one field to be the earliest, or the latest, of a collection
 * of fields.
 *
 * Built for a timesheet's two markers rather than reused from `withinSpan`, which asks the same
 * question of both ends of a span at once and so can only ever say an item lies "outside" it.
 * Asked one marker and one edge at a time instead, the caller's message can say plainly which
 * marker has to lead — or trail — the timesheet's own entries, which is what lets `startOfWork`
 * and `endOfWork` each carry a message specific to what they are required to be, rather than a
 * message generic enough to cover both.
 *
 * Both the bound and every offending item are marked, so the message is visible wherever the
 * technician is looking — on the marker row that is out of place, and on the entry row that put it
 * there. A collection-only error is added alongside them so the form still counts the conflict when
 * every field it could otherwise mark is readonly, which drops validation state entirely; see
 * `spanConflictSummary` in `chronologically-ordered.rule.ts` for the same reasoning.
 *
 * @param path the value carrying the bound and its collection
 * @param toFields reads the bound field, the collection, and which edge applies
 * @param config optional message
 */
export function boundsCollection<TValue, TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<TValue, SchemaPathRules.Supported, TPathKind>,
    toFields: (fieldTree: FieldTree<TValue>) => BoundsCollectionFields,
    config?: BoundsCollectionConfig,
): void {
    validateTree(path, (context) => {
        const { bound, items, edge } = toFields(context.fieldTree);
        const message = config?.message ?? ((): string => '');
        const conflicts = findBoundConflicts(
            bound().value(),
            items.map((item) => item().value()),
            edge,
        );

        const errors = conflicts.map((conflict): ValidationError.WithOptionalFieldTree => ({
            kind: 'boundedByCollection',
            message: message(conflict),
            fieldTree: conflict.index === undefined ? bound : items[conflict.index],
        }));

        // The bound's own conflict is always last — see findBoundConflicts — so it doubles as the
        // one this reports without a fieldTree, for the readonly case spanConflictSummary exists for.
        return conflicts.length === 0
            ? errors
            : [...errors, { kind: 'boundedByCollection', message: message(conflicts[conflicts.length - 1]) }];
    });
}
